/** @format */

import Page from "../../components/Page";
import {
  Button,
  Card,
  CircularProgress,
  Container,
  Grid,
  Stack,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Box,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import Iconify from "../../components/Iconify";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import React, { useEffect, useState } from "react";
import User from "../../dao/User";
import swal from "../../reusables/CustomSwal";
import ChangePasswordForm from "../../components/ChangePasswordForm";

export default function ProfileDetails() {
  const navigate = useNavigate();

  const [userDetails, setUserDetails] = useState({});
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [passwordFormOpen, setPasswordFormOpen] = useState(false);
  const [is2FASetup, setIs2FASetup] = useState(false);
  const [is2FAEnabled, setIs2FAEnabled] = useState(false);

  const getUserDetails = async () => {
    try {
      let result = await User.getSelf();
      setUserDetails(result);

      // Check 2FA status
      await check2FAStatus();

      setIsDataLoading(false);
    } catch (e) {
      let errorMessage = "An error has occurred. Please try again.";
      await swal.fire({
        title: "Error",
        text: errorMessage,
        icon: "error",
      });
      navigate(-1);
    }
  };

  const check2FAStatus = async () => {
    try {
      const data = await User.getTOTPStatus();
      if (data.success) {
        setIs2FAEnabled(data.totpEnabled);
      }
    } catch (error) {
      console.error("Error checking 2FA status:", error);
      // Don't show error for 2FA status check failure
    }
  };

  useEffect(() => {
    getUserDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Page title="Profile">
      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Typography variant="h4">Profile</Typography>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<Iconify icon="mdi:shield-key-outline" />}
              onClick={() => {
                if (is2FAEnabled) {
                  swal.fire({
                    title: "2FA is already enabled",
                    text: "Two-factor authentication is currently active on your account.",
                    icon: "info",
                  });
                } else {
                  setIs2FASetup(true);
                }
              }}
              color={is2FAEnabled ? "success" : "primary"}
            >
              {is2FAEnabled ? "2FA Enabled" : "Setup 2FA"}
            </Button>
            <Button
              variant="outlined"
              startIcon={<Iconify icon="akar-icons:key" />}
              onClick={() => setPasswordFormOpen(true)}
            >
              Change Password
            </Button>
            <Button
              variant="contained"
              component={RouterLink}
              to={`/profile/edit`}
              startIcon={<Iconify icon="akar-icons:pencil" />}
            >
              Edit
            </Button>
          </Stack>
        </Stack>
        <Card sx={{ p: 4 }}>
          {isDataLoading ? (
            <Stack alignItems="center">
              <CircularProgress />
            </Stack>
          ) : (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Stack spacing={1}>
                  <Typography
                    variant="p"
                    sx={{ color: "gray", fontSize: "0.8em" }}
                  >
                    First Name
                  </Typography>
                  <Typography variant="p">
                    {userDetails.profile.first_name}
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack spacing={1}>
                  <Typography
                    variant="p"
                    sx={{ color: "gray", fontSize: "0.8em" }}
                  >
                    Last Name
                  </Typography>
                  <Typography variant="p">
                    {userDetails.profile.last_name}
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Stack spacing={1}>
                  <Typography
                    variant="p"
                    sx={{ color: "gray", fontSize: "0.8em" }}
                  >
                    Job Title
                  </Typography>
                  <Typography variant="p">
                    {userDetails.profile.job_title}
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Stack spacing={1}>
                  <Typography
                    variant="p"
                    sx={{ color: "gray", fontSize: "0.8em" }}
                  >
                    Email
                  </Typography>
                  <Typography variant="p">
                    {userDetails.profile.email}
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Stack spacing={1}>
                  <Typography
                    variant="p"
                    sx={{ color: "gray", fontSize: "0.8em" }}
                  >
                    Phone Number
                  </Typography>
                  <Typography variant="p">
                    {userDetails.profile.phone_number}
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Stack spacing={1}>
                  <Typography
                    variant="p"
                    sx={{ color: "gray", fontSize: "0.8em" }}
                  >
                    Role
                  </Typography>
                  <Typography variant="p">{userDetails.role}</Typography>
                </Stack>
              </Grid>
             
            </Grid>
          )}
        </Card>
      </Container>
      {passwordFormOpen && (
        <ChangePasswordForm
          id={userDetails.user_id}
          open={passwordFormOpen}
          setOpen={setPasswordFormOpen}
        />
      )}
      {is2FASetup && (
        <Setup2FaPage
          open={is2FASetup}
          setOpen={setIs2FASetup}
          setIs2FAEnabled={setIs2FAEnabled}
        />
      )}
    </Page>
  );
}

function Setup2FaPage({ open, setOpen, setIs2FAEnabled }) {
  const [isLoading, setIsLoading] = useState(false);
  const [qrCodeURL, setQrCodeURL] = useState("");
  const [secret, setSecret] = useState("");
  const [verificationToken, setVerificationToken] = useState("");
  const [step, setStep] = useState(1); // 1: Setup, 2: Verify

  const setup2FA = async () => {
    setIsLoading(true);
    try {
      const data = await User.setupTOTP();
      if (data.success) {
        setQrCodeURL(data.qrCodeURL);
        setSecret(data.secret);
        setStep(2);
        await swal.fire({
          title: "Setup Initiated",
          text: "Please scan the QR code with your authenticator app and enter the verification code.",
          icon: "success",
        });
      } else {
        throw new Error(data.message || "Setup failed");
      }
    } catch (error) {
      let errorMessage = "An error has occurred. Please try again later.";
      if (error.message && error.message.includes("already set up")) {
        errorMessage = "2FA is already set up for this account.";
      }
      await swal.fire({
        title: "Error",
        text: errorMessage,
        icon: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const verify2FA = async () => {
    if (!verificationToken || verificationToken.length !== 6) {
      await swal.fire({
        title: "Invalid Token",
        text: "Please enter a 6-digit verification code.",
        icon: "warning",
      });
      return;
    }

    setIsLoading(true);
    try {
      const data = await User.verifyTOTP(verificationToken);
      if (data.success) {
        setIs2FAEnabled(true); // Update the state immediately
        await swal.fire({
          title: "2FA Enabled Successfully!",
          text: "Your two-factor authentication has been set up successfully.",
          icon: "success",
        });
        setOpen(false);
      } else {
        throw new Error(data.message || "Verification failed");
      }
    } catch (error) {
      let errorMessage = "Invalid verification code. Please try again.";
      if (error.message && error.message.includes("INVALID_TOTP")) {
        errorMessage = "The verification code is incorrect or expired.";
      }
      await swal.fire({
        title: "Verification Failed",
        text: errorMessage,
        icon: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setQrCodeURL("");
    setSecret("");
    setVerificationToken("");
    setOpen(false);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="h6">Setup Two-Factor Authentication</Typography>
          <IconButton onClick={handleClose}>
            <Iconify icon="eva:close-outline" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent>
        {step === 1 && (
          <Stack spacing={3} sx={{ py: 2 }}>
            <Stack spacing={2} alignItems="center">
              <Iconify
                icon="mdi:shield-key-outline"
                width={64}
                height={64}
                color="primary.main"
              />
              <Typography variant="h6" textAlign="center">
                Secure Your Account
              </Typography>
              <Typography
                variant="body2"
                textAlign="center"
                color="text.secondary"
              >
                Two-factor authentication adds an extra layer of security to
                your account. You'll need an authenticator app like Google
                Authenticator or Authy.
              </Typography>
            </Stack>

            <Stack spacing={2}>
              <Typography variant="subtitle1" fontWeight="bold">
                Before you start:
              </Typography>
              <Stack spacing={1}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Iconify
                    icon="eva:checkmark-circle-2-outline"
                    color="success.main"
                  />
                  <Typography variant="body2">
                    Install an authenticator app
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Iconify
                    icon="eva:checkmark-circle-2-outline"
                    color="success.main"
                  />
                  <Typography variant="body2">Have your phone ready</Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Iconify
                    icon="eva:checkmark-circle-2-outline"
                    color="success.main"
                  />
                  <Typography variant="body2">Keep this window open</Typography>
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        )}

        {step === 2 && (
          <Stack spacing={3} sx={{ py: 2 }}>
            <Typography variant="h6" textAlign="center">
              Scan QR Code
            </Typography>

            <Stack spacing={2} alignItems="center">
              {qrCodeURL && (
                <Box
                  component="img"
                  src={qrCodeURL}
                  alt="2FA QR Code"
                  sx={{
                    width: 200,
                    height: 200,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                  }}
                />
              )}

              <Typography
                variant="body2"
                textAlign="center"
                color="text.secondary"
              >
                Scan this QR code with your authenticator app
              </Typography>

              {secret && (
                <Stack spacing={1} alignItems="center">
                  <Typography variant="body2" fontWeight="bold">
                    Or enter this code manually:
                  </Typography>
                  <TextField
                    value={secret}
                    variant="outlined"
                    size="small"
                    InputProps={{
                      readOnly: true,
                      style: { fontFamily: "monospace", fontSize: "12px" },
                    }}
                    fullWidth
                  />
                </Stack>
              )}
            </Stack>

            <Stack spacing={2}>
              <Typography variant="subtitle1" textAlign="center">
                Enter verification code:
              </Typography>
              <TextField
                fullWidth
                label="6-digit code"
                value={verificationToken}
                onChange={(e) =>
                  setVerificationToken(
                    e.target.value.replace(/\D/g, "").slice(0, 6)
                  )
                }
                placeholder="123456"
                inputProps={{
                  maxLength: 6,
                  style: {
                    textAlign: "center",
                    fontSize: "1.2em",
                    letterSpacing: "0.2em",
                  },
                }}
                helperText="Enter the 6-digit code from your authenticator app"
              />
            </Stack>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        {step === 1 && (
          <LoadingButton
            variant="contained"
            onClick={setup2FA}
            loading={isLoading}
            startIcon={<Iconify icon="eva:shield-outline" />}
            fullWidth
            size="large"
          >
            Start Setup
          </LoadingButton>
        )}

        {step === 2 && (
          <Stack direction="row" spacing={2} width="100%">
            <Button
              variant="outlined"
              onClick={() => setStep(1)}
              disabled={isLoading}
              startIcon={<Iconify icon="eva:arrow-back-outline" />}
            >
              Back
            </Button>
            <LoadingButton
              variant="contained"
              onClick={verify2FA}
              loading={isLoading}
              disabled={!verificationToken || verificationToken.length !== 6}
              startIcon={<Iconify icon="eva:checkmark-outline" />}
              fullWidth
            >
              Verify & Enable 2FA
            </LoadingButton>
          </Stack>
        )}
      </DialogActions>
    </Dialog>
  );
}
